import { Modal } from "@/components/Modal";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { EmployeeRole } from "@/hooks/useMakersBoardData";
import { useRoles } from "@/hooks/useRoles";
import { db, supabase } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import { useEffect, useState } from "react";

type Office = Database["public"]["Tables"]["offices"]["Row"];

interface AddEmployeeModalProps {
  onClose: () => void;
  onEmployeeAdded: () => void;
}

export function AddEmployeeModal({ onClose, onEmployeeAdded }: AddEmployeeModalProps) {
  const { roles } = useRoles();
  const [offices, setOffices] = useState<Office[]>([]);
  const [name, setName] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<EmployeeRole[]>([]);
  const [weeklyHours, setWeeklyHours] = useState("40");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  useEffect(() => {
    db.offices.getActive().then(({ data }) => {
      if (data) setOffices(data);
    });
  }, []);

  const isValid = name.trim().length > 0 && officeId.length > 0 && selectedRoles.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setSaving(true);
    setError(null);

    try {
      const trimmedName = name.trim();
      const placeholderEmail = `placeholder-${Date.now()}@example.com`;
      const hours = Number(weeklyHours);
      const capacity = isNaN(hours) || hours < 0 || hours > 80 ? 40 : hours;

      const { data: insertedRow, error: insertError } = await supabase
        .from("employees")
        .insert({
          name: trimmedName,
          email: placeholderEmail,
          office_id: officeId,
          role: selectedRoles,
          weekly_capacity_hours: capacity,
          is_active: true,
          employee_number: null,
        })
        .select("id")
        .single();

      if (insertError) {
        setError(insertError.message);
        return;
      }

      if (insertedRow?.id) {
        markSelfEdit("employees", insertedRow.id);
      }

      onEmployeeAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create employee");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Employee" onClose={onClose} size="small">
      <form className="add-employee-form" onSubmit={handleSubmit}>
        {error && <div className="add-employee-form__error">{error}</div>}

        <label className="add-employee-form__field">
          <span className="add-employee-form__label">Name</span>
          <input
            type="text"
            className="add-employee-form__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jane Smith"
            autoFocus
          />
        </label>

        <label className="add-employee-form__field">
          <span className="add-employee-form__label">Office</span>
          <select
            className="add-employee-form__input"
            value={officeId}
            onChange={(e) => setOfficeId(e.target.value)}
          >
            <option value="">Select office...</option>
            {offices.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="add-employee-form__field">
          <legend className="add-employee-form__label">Role</legend>
          <div className="add-employee-form__role-options">
            {roles.map((role) => (
              <label key={role.id} className="add-employee-form__role-option">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles([...selectedRoles, role.id]);
                    } else {
                      setSelectedRoles(selectedRoles.filter((r) => r !== role.id));
                    }
                  }}
                />
                {role.display_name}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="add-employee-form__field">
          <span className="add-employee-form__label">Weekly capacity (hours)</span>
          <input
            type="number"
            className="add-employee-form__input"
            value={weeklyHours}
            onChange={(e) => setWeeklyHours(e.target.value)}
            min={0}
            max={80}
            step={0.5}
          />
        </label>

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={!isValid || saving}>
            {saving ? "Adding..." : "Add Employee"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
